import React, { useRef } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import type { Swiper as SwiperType } from "swiper";
import "swiper/css";
import "swiper/css/pagination";
import AnimatedSection from "./AnimatedSection";
import SectionHeader from "./SectionHeader";
import avatar1 from "../../../images/avatar/img01.jpg";
import avatar2 from "../../../images/avatar/img02.jpg";
import avatar3 from "../../../images/avatar/img03.jpg";
import avatar4 from "../../../images/avatar/img04.jpg";

interface Testimonial {
  id: number;
  name: string;
  company: string;
  rating: number;
  review: string;
  avatar: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: "Rahul Sharma",
    company: "FitLife Gym",
    rating: 5,
    review:
      "WebTech's SEO strategy doubled our organic traffic in 6 months. Their team is responsive, transparent, and truly understands local marketing.",
    avatar: avatar1,
  },
  {
    id: 2,
    name: "Priya Nair",
    company: "Bloom Boutique",
    rating: 5,
    review:
      "Our Meta ad campaigns now generate 3x more sales at half the cost per acquisition. Best digital marketing investment we've made.",
    avatar: avatar2,
  },
  {
    id: 3,
    name: "James Wilson",
    company: "TechStart Inc.",
    rating: 5,
    review:
      "From content marketing to Google Ads, WebTech handled everything. Lead quality improved dramatically within the first quarter.",
    avatar: avatar3,
  },
  {
    id: 4,
    name: "Anita Desai",
    company: "GreenLeaf Organics",
    rating: 5,
    review:
      "The monthly reports are clear and actionable. Our social media engagement grew 250% and brand awareness has never been stronger.",
    avatar: avatar4,
  },
];

const StarRating = ({ rating }: { rating: number }) => (
  <div className="dm-testimonial__stars" aria-label={`${rating} out of 5 stars`}>
    {Array.from({ length: 5 }, (_, i) => (
      <svg key={i} viewBox="0 0 20 20" aria-hidden="true">
        <path
          d="M10 2l2.4 4.9 5.4.8-3.9 3.8.9 5.4L10 15.8 4.2 17.9l.9-5.4L1.2 7.7l5.4-.8L10 2Z"
          fill={i < rating ? "#fbbf24" : "rgba(255,255,255,0.15)"}
        />
      </svg>
    ))}
  </div>
);

const DmTestimonials: React.FC = () => {
  const swiperRef = useRef<SwiperType | null>(null);

  return (
    <AnimatedSection as="section" className="dm-section dm-section--alt" aria-labelledby="dm-testimonials-title">
      <div className="dm-container">
        <SectionHeader
          eyebrow="Testimonials"
          title="What Our Clients Say"
          text="Trusted by businesses across India for results-driven digital marketing."
        />
      </div>

      <AnimatedSection className="dm-testimonials__slider-wrap" delay={0.1}>
        <button
          type="button"
          className="dm-testimonials__nav dm-testimonials__nav--prev"
          aria-label="Previous testimonial"
          onClick={() => swiperRef.current?.slidePrev()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>

        <Swiper
          modules={[Autoplay, Pagination]}
          loop
          speed={600}
          spaceBetween={24}
          autoplay={{ delay: 5000, disableOnInteraction: false }}
          pagination={{ clickable: true }}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          breakpoints={{
            1200: { slidesPerView: 3 },
            768: { slidesPerView: 2 },
            0: { slidesPerView: 1 },
          }}
          className="dm-testimonials__slider"
        >
          {testimonials.map((item) => (
            <SwiperSlide key={item.id}>
              <article className="dm-testimonial-card">
                <div className="dm-testimonial-card__author">
                  <img src={item.avatar} alt="" className="dm-testimonial-card__avatar" />
                  <div>
                    <h3>{item.name}</h3>
                    <span>{item.company}</span>
                  </div>
                </div>
                <StarRating rating={item.rating} />
                <p>&ldquo;{item.review}&rdquo;</p>
              </article>
            </SwiperSlide>
          ))}
        </Swiper>

        <button
          type="button"
          className="dm-testimonials__nav dm-testimonials__nav--next"
          aria-label="Next testimonial"
          onClick={() => swiperRef.current?.slideNext()}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
        </button>
      </AnimatedSection>
    </AnimatedSection>
  );
};

export default DmTestimonials;
